import React, { useState, useEffect } from 'react';
import { Plus, FileText, Check, AlertCircle } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { c, regionConfig } from '@/config/region';

interface PaymentsContentProps {
  animationDelay?: number;
}

const invoices = [
  { name: 'Web Redesign', number: 'INV-001', customer: 'Acme', date: 'Jan 10', amount: c('$4,500'), status: 'Paid' },
  { name: 'Brand Strategy', number: 'INV-002', customer: 'TechStart', date: 'Jan 8', amount: c('$2,800'), status: 'Due' },
  { name: 'Marketing', number: 'INV-003', customer: 'Growth', date: 'Jan 5', amount: c('$6,200'), status: 'Paid' },
];

const PaymentsContent: React.FC<PaymentsContentProps> = ({ animationDelay = 0 }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [stats, setStats] = useState({ draft: 0, due: 0, received: 0, overdue: 0 });
  const [showInvoices, setShowInvoices] = useState<number[]>([]);
  const [showNewInvoice, setShowNewInvoice] = useState(false);

  useEffect(() => {
    const duration = 1200;
    const steps = 20;
    let step = 0;
    
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      setStats({
        draft: Math.round(2.5 * progress * 1000) / 1000,
        due: Math.round(4.8 * progress * 1000) / 1000,
        received: Math.round(12.5 * progress * 1000) / 1000,
        overdue: 0
      });
      if (step >= steps) clearInterval(timer);
    }, duration / steps);

    setTimeout(() => setShowInvoices([0]), animationDelay + 800);
    setTimeout(() => setShowInvoices([0, 1]), animationDelay + 1000);
    setTimeout(() => setShowInvoices([0, 1, 2]), animationDelay + 1200);
    setTimeout(() => setShowNewInvoice(true), animationDelay + 1500);

    return () => clearInterval(timer);
  }, [animationDelay]);

  const cur = regionConfig.currency;
  const statCards = [
    { count: 2, label: 'Draft', value: `${cur}${stats.draft.toFixed(1)}K`, color: '#6B7280' },
    { count: 1, label: 'Due', value: `${cur}${stats.due.toFixed(1)}K`, color: '#F59E0B' },
    { count: 3, label: 'Recv', value: `${cur}${stats.received.toFixed(1)}K`, color: '#10B981' },
    { count: 0, label: 'Late', value: `${cur}0`, color: '#EF4444' },
  ];

  return (
    <div 
      className="flex-1 p-1.5 sm:p-2 lg:p-4 overflow-hidden"
      style={{ 
        backgroundColor: isDark ? '#222222' : '#F1F5F9',
        animation: `fadeIn 0.4s ease-out ${animationDelay}ms both`
      }}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between mb-1.5 sm:mb-2 lg:mb-4"
        style={{ animation: `fadeIn 0.4s ease-out ${animationDelay + 100}ms both` }}
      >
        <div>
          <h3 
            className="text-[8px] sm:text-[10px] lg:text-sm font-bold"
            style={{ color: isDark ? '#FFFFFF' : '#111111' }}
          >
            Invoices
          </h3>
          <p 
            className="text-[6px] sm:text-[7px] lg:text-[9px] hidden sm:block"
            style={{ color: isDark ? '#888888' : '#666666' }}
          >
            Manage invoices
          </p>
        </div>
        <button 
          className="flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[6px] sm:text-[8px] lg:text-[9px] font-medium text-white"
          style={{ 
            backgroundColor: '#3B82F6',
            animation: `pulseGlow 2s ease-in-out infinite`
          }}
        >
          <Plus className="w-2 h-2 sm:w-2.5 sm:h-2.5 lg:w-3 lg:h-3" />
          New
        </button>
      </div>

      {/* Stats Cards */}
      <div 
        className="grid grid-cols-4 gap-1 sm:gap-1.5 lg:gap-3 mb-1.5 sm:mb-2 lg:mb-4"
        style={{ animation: `fadeIn 0.4s ease-out ${animationDelay + 200}ms both` }}
      >
        {statCards.map((card, index) => (
          <div 
            key={index}
            className="p-1 sm:p-1.5 lg:p-3 rounded-lg"
            style={{ 
              backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
              border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
              animation: `scaleIn 0.4s ease-out ${animationDelay + 300 + (index * 80)}ms both`
            }}
          >
            <span 
              className="text-[5px] sm:text-[6px] lg:text-[9px] block mb-0.5"
              style={{ color: card.color }}
            >
              {card.count} {card.label}
            </span>
            <span 
              className="text-[8px] sm:text-[10px] lg:text-lg font-bold block"
              style={{ color: isDark ? '#FFFFFF' : '#111111' }}
            >
              {card.value}
            </span>
          </div>
        ))}
      </div>

      {/* Table */}
      <div 
        className="rounded-lg overflow-hidden"
        style={{ 
          backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
          border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
          animation: `fadeIn 0.4s ease-out ${animationDelay + 500}ms both`
        }}
      >
        {/* Table Header */}
        <div 
          className="grid grid-cols-3 lg:grid-cols-5 gap-1 lg:gap-2 px-1.5 sm:px-2 lg:px-3 py-1 sm:py-1.5 lg:py-2 text-[5px] sm:text-[6px] lg:text-[9px] font-medium border-b"
          style={{ 
            backgroundColor: isDark ? '#2A2A2A' : '#F9FAFB',
            borderColor: isDark ? '#333333' : '#E5E5E5',
            color: isDark ? '#888888' : '#666666'
          }}
        >
          <span>Name</span>
          <span className="hidden lg:block">Customer</span>
          <span className="hidden lg:block">Date</span>
          <span>Amount</span>
          <span>Status</span>
        </div>

        {/* Table Body */}
        <div>
          {invoices.map((invoice, index) => (
            <div 
              key={index}
              className="grid grid-cols-3 lg:grid-cols-5 gap-1 lg:gap-2 px-1.5 sm:px-2 lg:px-3 py-1 sm:py-1.5 lg:py-2 text-[6px] sm:text-[7px] lg:text-[9px] border-b last:border-0"
              style={{ 
                borderColor: isDark ? '#333333' : '#E5E5E5',
                opacity: showInvoices.includes(index) ? 1 : 0,
                transform: showInvoices.includes(index) ? 'translateX(0)' : 'translateX(-20px)',
                transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
              }}
            >
              <span style={{ color: isDark ? '#FFFFFF' : '#111111' }}>{invoice.name}</span>
              <span className="hidden lg:block" style={{ color: isDark ? '#888888' : '#666666' }}>{invoice.customer}</span>
              <span className="hidden lg:block" style={{ color: isDark ? '#888888' : '#666666' }}>{invoice.date}</span>
              <span className="font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>{invoice.amount}</span>
              <span 
                className="inline-flex items-center gap-0.5 px-0.5 sm:px-1 lg:px-1.5 py-0.5 rounded text-[5px] sm:text-[6px] lg:text-[8px] font-medium w-fit"
                style={{ 
                  backgroundColor: invoice.status === 'Paid' ? '#D1FAE5' : '#FEF3C7',
                  color: invoice.status === 'Paid' ? '#059669' : '#D97706'
                }}
              >
                {invoice.status === 'Paid' ? <Check className="w-1.5 h-1.5 sm:w-2 sm:h-2" /> : <AlertCircle className="w-1.5 h-1.5 sm:w-2 sm:h-2" />}
                <span className="hidden sm:inline">{invoice.status}</span>
              </span>
            </div>
          ))}
        </div>

        {/* New Invoice Notification - hidden on mobile */}
        {showNewInvoice && (
          <div 
            className="hidden sm:flex mx-2 lg:mx-3 mb-2 lg:mb-3 p-1.5 lg:p-2 rounded-lg items-center gap-1.5 lg:gap-2"
            style={{ 
              backgroundColor: '#EEF2FF',
              border: '1px solid #C7D2FE',
              animation: 'slideInFromBottom 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
            }}
          >
            <FileText className="w-2.5 h-2.5 lg:w-3.5 lg:h-3.5" style={{ color: '#6366F1' }} />
            <span className="text-[7px] lg:text-[9px] font-medium" style={{ color: '#4338CA' }}>
              {c('New invoice: $3,200')}
            </span>
            <Check 
              className="w-2 h-2 lg:w-3 lg:h-3 ml-auto" 
              style={{ 
                color: '#10B981',
                animation: 'bounceIn 0.5s ease-out'
              }} 
            />
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 5px rgba(59, 130, 246, 0.3); }
          50% { box-shadow: 0 0 15px rgba(59, 130, 246, 0.5); }
        }
        @keyframes slideInFromBottom {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounceIn {
          0% { transform: scale(0); }
          50% { transform: scale(1.3); }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default PaymentsContent;
